import { useEffect, type ReactElement } from "react";
import { Link, useSearchParams } from "react-router";

import { queryValue } from "../../app/navigation.js";
import { localReturnUrl, rememberReturnAfterRegister, withReturnUrl } from "../../app/return-after-register.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { MessagePage } from "../../components/message-page.js";
import { NewTabIcon } from "../../components/new-tab-icon.js";
import { useDetail } from "../../components/notice.js";
import { describedBy, errorIdOf } from "../../forms/described-by.js";
import { FormSummary, TextField } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";
import { registerRules } from "../../forms/view-model-rules.js";

import { accountApi, accountUrls } from "./urls.js";

/**
 * アカウント登録。<b>招待メールから来たら合言葉（?passphrase=）を隠し欄で送る</b>——
 * 招待と一致すればメール確認を省き、そのまま承諾画面へ進む（判定はサーバの RegistrationFlow）。
 * <b>戻り先（?returnUrl=。料金プランの「この内容で申し込む」）はこのブラウザに控える</b>——メールの確認の後の
 * 「ログイン画面へ」で付け直す（<c>app/return-after-register.ts</c>）。「ログイン」のボタンにもそのまま付ける。
 */
export function RegisterPage(): ReactElement {
    usePageMeta("アカウント登録");
    const form = useApiForm(accountApi.register, registerRules);
    const [search] = useSearchParams();
    const passphrase = queryValue(search, "passphrase") ?? "";
    const agreeError = form.fieldError("Agree");
    const returnUrl = localReturnUrl(queryValue(search, "returnUrl"));
    useEffect(() => {
        if (returnUrl !== undefined) {
            rememberReturnAfterRegister(returnUrl);
        }
    }, [returnUrl]);

    return (
        <div className="page max-w-md">
            <div className="panel">
                <h1 className="page-title">アカウント登録</h1>
                <p className="page-lead">
                    {passphrase === ""
                        ? "ご登録のメールアドレス宛に確認メールをお送りします。"
                        : "招待を承諾するには、アカウントを登録してください。すでにアカウントをお持ちの方はログインしてください。"}
                </p>

                <form method="post" noValidate onSubmit={form.onSubmit} onChange={form.onChange} onBlur={form.onBlur}>
                    <FormSummary messages={form.summary} />
                    <input type="hidden" name="Passphrase" value={passphrase} />

                    <TextField
                        label="メールアドレス" name="Email" type="email" required
                        placeholder="mail@example.com" autoComplete="username" autoFocus
                        error={form.fieldError("Email")}
                    />
                    <TextField
                        label="パスワード" name="Password" type="password" required
                        placeholder="••••••••••" autoComplete="new-password"
                        help="10 文字以上で入力してください。"
                        error={form.fieldError("Password")}
                    />
                    <TextField
                        label="パスワードの確認入力" name="ConfirmPassword" type="password"
                        placeholder="••••••••••" autoComplete="new-password"
                        wrapperClassName="mb-7"
                        error={form.fieldError("ConfirmPassword")}
                    />

                    {/* 同意の前に規約の本文と、入れた情報の使い道を示す（個人情報保護法の利用目的の明示）。見た目は申し込みの同意と同じ */}
                    <div className="mb-7">
                        <p className="mb-3 text-sm text-slate-700">
                            ご登録には、
                            <a href="/Home/Terms" target="_blank" rel="noopener" className="inline-link">利用規約<NewTabIcon /></a>
                            への同意が必要です。
                            ご登録いただいた情報は、本サービスのご提供とご連絡に利用します。取り扱いは
                            <a href="https://implem.co.jp/privacy" target="_blank" rel="noopener noreferrer" className="inline-link">プライバシーポリシー<NewTabIcon /></a>
                            をご覧ください。
                        </p>
                        {/* 値は DOM が持つ（useApiForm）。チェックしなければ送らず、サーバでは Agree が false になって断られる */}
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                className="form-check" type="checkbox" id="Agree" name="Agree" value="true"
                                aria-invalid={agreeError === undefined ? undefined : true}
                                {...describedBy("Agree", { help: false, invalid: agreeError !== undefined, required: true })}
                            />
                            <span>利用規約に同意する</span>
                        </label>
                        <span
                            className={`form-error ${agreeError === undefined ? "field-validation-valid" : "field-validation-error"}`}
                            id={errorIdOf("Agree")}
                            data-valmsg-for="Agree"
                        >
                            {agreeError}
                        </span>
                    </div>

                    <button type="submit" className="btn-primary w-full py-3" disabled={form.busy}>登録する</button>
                </form>

                {/* 操作の入口は文中のリンクにせず、短い説明とボタンを並べる（docs/01 §6） */}
                <div className="panel-footer flex items-center justify-between gap-4">
                    <p>すでにアカウントをお持ちの方</p>
                    <Link to={withReturnUrl(accountUrls.login, returnUrl)} className="btn-secondary btn-sm shrink-0">ログイン</Link>
                </div>
            </div>
        </div>
    );
}

/**
 * 確認メールの送信待ち。<b>送ったアドレスは API の detail で受け取る</b>（URL に載せない）。
 * 直に開いたときはアドレスを出さない。
 */
export function ShowWaitMessagePage(): ReactElement {
    const email = useDetail();

    return (
        <MessagePage
            title="確認メールをお送りしました"
            actions={
                // slate-400 はプレースホルダの明るさ（白地で 3.4）。読ませる文なので slate-500 にする
                <p className="text-xs text-slate-500">メールが届かない場合は、迷惑メールフォルダをご確認ください。</p>
            }
        >
            {email === undefined || email === ""
                ? null
                : (
                    <>
                        <span className="font-medium text-slate-900">{email}</span>
                        宛に確認メールをお送りしました。<br />
                    </>
                )}
            メール内のリンクを開いて、アカウント登録を完了してください。
        </MessagePage>
    );
}
